import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { scroll } from 'motion';
import { Container, Layout } from '@fiverr-private/layout_components';
import { Heading } from '@fiverr-private/typography';
import { Video } from '@fiverr-private/media';
import { IconButton } from '@fiverr-private/button';
import { PlaySolidIcon, PauseSolidIcon } from '@fiverr-private/visuals';
import SearchBar from '../SearchBar';
import SearchCategoriesLinks from '../SearchCategoriesLinks';
import Partners from '../Partners';
import { VIDEO_SOURCES, VIDEO_POSTER } from './constants';
import {
  headingClass,
  primaryTextClass,
  secondaryTextClass,
  contentContainerClass,
  videoWrapperClass,
  videoPlaceholderClass,
  overlayClass,
  sectionClass,
} from './styles.ve.css';

const TopFolder: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const handleVideoButtonClick = () => {
    if (!videoRef.current) {
      return;
    }

    if (!isPlaying) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia && window?.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery?.matches);

    const handleChange = () => setPrefersReducedMotion(mediaQuery?.matches);
    mediaQuery.addEventListener('change', handleChange);

    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useLayoutEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const section = sectionRef.current;
    const videoWrapper = videoWrapperRef.current;

    if (!section || !videoWrapper) {
      return;
    }

    videoWrapper.style.clipPath = 'inset(0% 0% 0% 0%)';

    const sectionHeight = section.offsetHeight;

    // Video animation - clip from top only, 2x faster than scroll
    const cleanup = scroll(
      (progress: number) => {
        const topClip = Math.min(Math.round(progress * 2 * 100 * 10) / 10, 100); // 0% to 100%

        videoWrapper.style.clipPath = `inset(${topClip}% 0% 0% 0%)`;
      },
      {
        target: section,
        offset: ['start start', `${sectionHeight}px start`],
      }
    );

    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }

      if (videoWrapper) {
        videoWrapper.style.clipPath = '';
      }
    };
  }, [prefersReducedMotion]);

  return (
    <Container as="section" className={sectionClass} ref={sectionRef}>
      <Container className={videoWrapperClass} ref={videoWrapperRef}>
        {prefersReducedMotion && (
          <Container
            style={{ background: `url(${VIDEO_POSTER}) center/cover no-repeat #000` }}
            className={videoPlaceholderClass}
            aria-hidden
          />
        )}
        {!prefersReducedMotion && (
          <Video
            width="100%"
            height="100%"
            autoPlay
            playsInline
            preload="auto"
            poster={VIDEO_POSTER}
            muted
            loop={true}
            controls={false}
            ref={videoRef}
            sources={[isMobile ? VIDEO_SOURCES.mobile : VIDEO_SOURCES.desktop]}
            aria-hidden
          />
        )}
      </Container>

      <Container className={overlayClass} aria-hidden />

      <Heading className={headingClass} as="h1">
        <Layout as="span" className={primaryTextClass}>
          Do more
        </Layout>
        <Layout as="span" className={secondaryTextClass}>
          with Fiverr&apos;s top freelancers
        </Layout>
      </Heading>

      <Container className={contentContainerClass} marginX="auto" marginY="0">
        <SearchBar />
        <SearchCategoriesLinks />
        <Partners />
      </Container>

      {!isMobile && (
        <Container position="absolute" right={59} bottom={25} zIndex="2">
          <IconButton onClick={handleVideoButtonClick} intent="primary" aria-label="Start/Pause video" shape="circle">
            {isPlaying ? <PauseSolidIcon color="white" /> : <PlaySolidIcon color="white" />}
          </IconButton>
        </Container>
      )}
    </Container>
  );
};

export default TopFolder;
